import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { services } from '../mock';
import { useToast } from '../hooks/use-toast';
import ContactInfo from './ContactInfo';
import { TextField, SelectField, TextAreaField } from './FormFields';

const initialForm = { name: '', email: '', business: '', service: '', message: '' };

const validate = (form) => Boolean(form.name && form.email && form.message);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) {
      toast({
        title: 'Missing details',
        description: 'Please fill in your name, email and message.',
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, form);
      toast({ title: 'Enquiry sent', description: "Thanks — I'll be in touch shortly." });
      setForm(initialForm);
    } catch (err) {
      toast({
        title: 'Could not send',
        description: 'Please check your email address, or call me on 020 4027 4459.',
      });
    } finally {
      setSending(false);
    }
  };

  const serviceOptions = services.map((s) => ({ value: s.title, label: s.title }));

  return (
    <section id="contact" className="bg-[#f4efe6] pt-24 md:pt-32 pb-12 md:pb-16">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 fade-up">
            <div className="eyebrow label-caps mb-6">Get in touch</div>
            <h2 className="font-serif text-[#201d1a] text-[36px] md:text-[52px] leading-[1.06] tracking-tight">
              Let&apos;s talk about what your business needs.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.7] text-[#4a423b] max-w-[440px]">
              Send a note below, or reach out directly. There&apos;s no obligation — just a straight conversation about what your business needs.
            </p>
            <ContactInfo />
          </div>

          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-[#f8f4ec] border border-[#e5dcc9] p-8 md:p-12 fade-up"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <TextField label="Name" name="name" value={form.name} onChange={onChange} placeholder="Your name" />
              <TextField label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.co.nz" />
              <TextField label="Business (optional)" name="business" value={form.business} onChange={onChange} placeholder="Company name" />
              <SelectField
                label="Service of interest"
                name="service"
                value={form.service}
                onChange={onChange}
                options={serviceOptions}
                placeholder="Choose a service"
              />
              <TextAreaField
                label="How can I help?"
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="A little about your business and what’s on your plate."
              />
            </div>

            <div className="mt-10">
              <button type="submit" className="btn-rose" disabled={sending} data-testid="contact-submit-button">
                {sending ? 'Sending…' : 'Send enquiry'} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
